import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Printer, ArrowLeft, Loader2, CheckCircle2, Clock } from "lucide-react";
import { getPayoutStatement } from "../services/payments";
import { fmtUSD } from "../lib/pricing";
import { Button } from "../components/ui/button";

// Designer-facing payout statement. It NEVER shows the platform commission or the
// total the lab paid — only the designer's own net earnings and payout status.
export default function PayoutStatement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPayoutStatement(sessionId).then(setData).catch(() => setData(false)).finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  if (!data) return <div className="py-24 text-center text-muted-foreground" data-testid="statement-not-found">{t("statement.notFound")}</div>;

  const paid = data.payout_status === "paid";

  const Row = ({ label, value, strong }) => (
    <div className={`flex justify-between py-2 ${strong ? "border-t border-border font-semibold" : ""}`}>
      <span className={strong ? "" : "text-muted-foreground"}>{label}</span>
      <span data-testid={`statement-${label}`}>{value}</span>
    </div>
  );

  return (
    <div className="mx-auto max-w-lg px-4 py-10" data-testid="payout-statement-page">
      <div className="mb-4 flex items-center justify-between print:hidden">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="statement-back"><ArrowLeft className="mr-1.5 h-4 w-4" /> {t("common.back")}</Button>
        <Button size="sm" className="rounded-full bg-brand text-white hover:bg-brand/90" onClick={() => window.print()} data-testid="statement-print"><Printer className="mr-1.5 h-4 w-4" /> {t("statement.print")}</Button>
      </div>

      <div className="rounded-xl border border-border bg-card p-8 shadow-sm">
        <div className="flex items-center justify-between border-b border-border pb-4">
          <div>
            <h1 className="font-heading text-xl font-bold tracking-tight">DentalCAD Connect</h1>
            <p className="text-sm text-muted-foreground">{t("statement.title")}</p>
          </div>
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${paid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="statement-status-badge">
            {paid ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
            {t(`admin.payoutStatus.${data.payout_status}`, data.payout_status)}
          </span>
        </div>

        <div className="mt-4 space-y-0.5 text-sm">
          <Row label={t("statement.caseId")} value={data.case_number || data.case_id || "—"} />
          <Row label={t("statement.lab")} value={data.lab_name || "—"} />
          {data.payout_paid_at && <Row label={t("statement.payoutDate")} value={new Date(data.payout_paid_at).toLocaleString()} />}
        </div>

        <div className="mt-6 space-y-0.5 text-sm">
          <Row label={t("statement.netEarnings")} value={fmtUSD(data.designer_net)} strong />
        </div>

        <div className="mt-6 border-t border-border pt-4 text-xs text-muted-foreground">
          <p>{t("statement.payoutStatus")}: {t(`admin.payoutStatus.${data.payout_status}`, data.payout_status)}</p>
          {data.payout_reference && <p className="mt-1">{t("statement.reference")}: <span className="font-mono">{data.payout_reference}</span></p>}
          {!paid && <p className="mt-2 text-amber-700">{t("statement.pending")}</p>}
        </div>
      </div>
    </div>
  );
}
